import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Check as ApproveIcon,
  Close as RejectIcon,
  Delete as DeleteIcon,
  Comment as CommentIcon,
  Pending as PendingIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
} from '@mui/icons-material';
import commentsService, { Comment, CommentStats } from '../services/commentsService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const Comments: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [pendingComments, setPendingComments] = useState<Comment[]>([]);
  const [allComments, setAllComments] = useState<Comment[]>([]);
  const [stats, setStats] = useState<CommentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: string | null }>({
    open: false,
    id: null,
  });

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [pending, all, statsData] = await Promise.all([
        commentsService.getPending(),
        commentsService.getAll(),
        commentsService.getStats(),
      ]);
      setPendingComments(pending);
      setAllComments(all);
      setStats(statsData);
    } catch (err) {
      setError('Erro ao carregar comentários');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleApprove = async (id: string) => {
    try {
      await commentsService.approve(id);
      loadData();
    } catch (err) {
      setError('Erro ao aprovar comentário');
    }
  };

  const handleReject = async (id: string) => {
    try {
      await commentsService.reject(id);
      loadData();
    } catch (err) {
      setError('Erro ao rejeitar comentário');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.id) return;
    try {
      await commentsService.delete(deleteDialog.id);
      setDeleteDialog({ open: false, id: null });
      loadData();
    } catch (err) {
      setError('Erro ao deletar comentário');
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'pending':
        return <Chip label="Pendente" color="warning" size="small" icon={<PendingIcon />} />;
      case 'approved':
        return <Chip label="Aprovado" color="success" size="small" icon={<ApprovedIcon />} />;
      case 'rejected':
        return <Chip label="Rejeitado" color="error" size="small" icon={<RejectedIcon />} />;
      default:
        return null;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderCommentsTable = (comments: Comment[], showActions: boolean = true) => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Usuário</TableCell>
            <TableCell>Devocional</TableCell>
            <TableCell>Comentário</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Data</TableCell>
            {showActions && <TableCell align="right">Ações</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {comments.length === 0 ? (
            <TableRow>
              <TableCell colSpan={showActions ? 6 : 5} align="center">
                <Typography color="text.secondary" sx={{ py: 4 }}>
                  Nenhum comentário encontrado
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            comments.map((comment) => (
              <TableRow key={comment.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {comment.user?.name || 'Usuário removido'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {comment.user?.email}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                    {comment.devotional?.title || 'Devocional removido'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      maxWidth: 300,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {comment.content}
                  </Typography>
                </TableCell>
                <TableCell>{getStatusChip(comment.status)}</TableCell>
                <TableCell>
                  <Typography variant="caption">{formatDate(comment.createdAt)}</Typography>
                </TableCell>
                {showActions && (
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      {comment.status === 'pending' && (
                        <>
                          <Tooltip title="Aprovar">
                            <IconButton
                              color="success"
                              size="small"
                              onClick={() => handleApprove(comment.id)}
                            >
                              <ApproveIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Rejeitar">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleReject(comment.id)}
                            >
                              <RejectIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title="Deletar">
                        <IconButton
                          color="default"
                          size="small"
                          onClick={() => setDeleteDialog({ open: true, id: comment.id })}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
        Moderação de Comentários
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <CommentIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" fontWeight={700}>
                {stats?.total || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <PendingIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
              <Typography variant="h4" fontWeight={700} color="warning.main">
                {stats?.pending || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pendentes
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <ApprovedIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" fontWeight={700} color="success.main">
                {stats?.approved || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Aprovados
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <RejectedIcon sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
              <Typography variant="h4" fontWeight={700} color="error.main">
                {stats?.rejected || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Rejeitados
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Pendentes
                {stats?.pending ? (
                  <Chip label={stats.pending} size="small" color="warning" />
                ) : null}
              </Box>
            }
          />
          <Tab label="Todos os Comentários" />
        </Tabs>
      </Paper>

      <TabPanel value={tab} index={0}>
        {renderCommentsTable(pendingComments, true)}
      </TabPanel>

      <TabPanel value={tab} index={1}>
        {renderCommentsTable(allComments, true)}
      </TabPanel>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null })}>
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir este comentário? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Comments;
